import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { MESSAGES } from '../../../utils/notify';
import { pick } from '../../../shared/offer-domain/pick';

// REST client for `api/gtc` (be#453). Every response is the
// `{ success, message, data, errors }` envelope; callers get `data`.

const request = async (method, path, { body, params } = {}) => {
  const token = getAuthToken();
  if (!token) throw new Error(MESSAGES.authRequired);

  const res = await axios({
    method,
    url: `${api.API_URL}${path}`,
    data: body,
    params,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  // Global axios carries TWO unwrapping response interceptors
  // (helpers/api_helper.js and core/api_helper.js), so the result is the raw
  // response, the envelope, or already `data`, depending on which loaded.
  let payload = res;
  if (payload && payload.headers && 'status' in payload) payload = payload.data;
  if (payload && typeof payload === 'object' && 'success' in payload) {
    return pick(payload, 'data', 'Data');
  }
  return payload ?? null;
};

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Month is an int 1-12 on the wire.
export const monthName = month => MONTHS[Number(month) - 1] || '';

const THIS_YEAR = new Date().getFullYear();
export const YEARS = Array.from({ length: 11 }, (_, i) =>
  String(THIS_YEAR - 5 + i)
);

export const STATUS = { ACTIVE: 'Active', INACTIVE: 'Inactive' };
// publishStatus int on the wire; labels come back as publishStatusLabel.
export const STATUS_PUBLISH = { DRAFT: 0, PUBLISH: 1, UNPUBLISH: 2 };
export const PUBLISH_LABEL = { 0: 'Draft', 1: 'Publish', 2: 'Unpublish' };
// Status -> pill tone, explicit (not label-guessed).
export const STATUS_TONE = { Active: 'success', Inactive: 'muted' };
export const PUBLISH_TONE = {
  Publish: 'success',
  Draft: 'warning',
  Unpublish: 'danger',
};
export const publishLabel = row =>
  row?.publishStatusLabel || PUBLISH_LABEL[row?.publishStatus] || '-';

// Paged list -> { items, totalItems, totalPages, pageNumber, pageSize }.
// params: PageNumber, PageSize, Search, IsActive, PublishStatus, Selectable,
// SortBy, IsDescending.
const list = params => request('get', '/gtc', { params });
// BE clamps PageSize to 100, so walk every page (Active + Publish only).
const listSelectable = async params => {
  const items = [];
  let totalItems = 0;
  for (let page = 1; ; page += 1) {
    const data = await list({
      ...params,
      PageNumber: page,
      PageSize: 100,
      Selectable: true,
    });
    const chunk = pick(data, 'items', 'Items') || [];
    items.push(...chunk);
    totalItems = pick(data, 'totalItems', 'TotalItems') ?? items.length;
    const totalPages = pick(data, 'totalPages', 'TotalPages') || 1;
    if (page >= totalPages || !chunk.length) break;
  }
  return { items, totalItems };
};
const getById = id => request('get', `/gtc/${id}`);
const create = body => request('post', '/gtc', { body });
const update = (id, body) => request('put', `/gtc/${id}`, { body });
const publish = id => request('post', `/gtc/${id}/publish`);
const unpublish = id => request('post', `/gtc/${id}/unpublish`);
const setDraft = id => request('post', `/gtc/${id}/draft`);
const copy = (id, body) => request('post', `/gtc/${id}/copy`, { body });

const createCategory = (gtcId, body) =>
  request('post', `/gtc/${gtcId}/categories`, { body });
const updateCategory = (categoryId, body) =>
  request('put', `/gtc/categories/${categoryId}`, { body });
const deleteCategory = categoryId =>
  request('delete', `/gtc/categories/${categoryId}`);
const createArticle = (categoryId, body) =>
  request('post', `/gtc/categories/${categoryId}/articles`, { body });
const updateArticle = (articleId, body) =>
  request('put', `/gtc/articles/${articleId}`, { body });
const deleteArticle = articleId =>
  request('delete', `/gtc/articles/${articleId}`);
const createClause = (articleId, body) =>
  request('post', `/gtc/articles/${articleId}/clauses`, { body });
const updateClause = (clauseId, body) =>
  request('put', `/gtc/clauses/${clauseId}`, { body });
const deleteClause = clauseId => request('delete', `/gtc/clauses/${clauseId}`);

// Supplier (Mitra) amendment.
const listAmend = params => request('get', '/gtc/amend', { params });
const getAmend = id => request('get', `/gtc/amend/${id}`);
// items: [{ gtcClauseId, isAmend, amendDetail }] -> fresh supplier detail.
const saveAmend = (id, items) =>
  request('put', `/gtc/amend/${id}`, { body: items });

// Tender flow.
const getProcurementGtc = procurementId =>
  request('get', `/gtc/procurement/${procurementId}`);
const getProcurementMitraGtc = (procurementId, procurementMitraId) =>
  request(
    'get',
    `/gtc/procurement/${procurementId}/mitra/${procurementMitraId}`
  );

// Master.Incoterm options for the GTC header (info only).
const listIncoterms = async () => {
  const data = await request('get', '/incoterm', {
    params: { PageNumber: 1, PageSize: 100 },
  });
  const items = Array.isArray(data) ? data : pick(data, 'items', 'Items');
  return Array.isArray(items) ? items : [];
};

export const gtcApi = {
  list,
  listSelectable,
  getById,
  create,
  update,
  publish,
  unpublish,
  setDraft,
  copy,
  createCategory,
  updateCategory,
  deleteCategory,
  createArticle,
  updateArticle,
  deleteArticle,
  createClause,
  updateClause,
  deleteClause,
  listAmend,
  getAmend,
  saveAmend,
  getProcurementGtc,
  getProcurementMitraGtc,
  listIncoterms,
};

export default gtcApi;
